import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Chave de site de teste da Cloudflare: aprova qualquer visitante. Só vale em desenvolvimento,
// para a tela funcionar localmente sem configurar nada; o backend de dev usa o par secreto dela.
const DEV_TEST_SITE_KEY = '1x00000000000000000000AA';

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY
    || (process.env.NODE_ENV === 'development' ? DEV_TEST_SITE_KEY : '');

let scriptPromise = null;

/**
 * Carrega o script da Cloudflare uma única vez, mesmo com vários widgets na página ou com a
 * tela montando de novo ao navegar.
 */
const loadTurnstileScript = () => {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(window.turnstile);
            script.onerror = () => {
                // Permite nova tentativa numa próxima montagem (rede instável, bloqueador).
                scriptPromise = null;
                reject(new Error('turnstile_load_failed'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

/**
 * Captcha Cloudflare Turnstile das telas públicas que criam cadastro.
 *
 * Entrega o token por onToken; token vazio significa expirado ou com erro, e o envio deve
 * esperar um novo. O token é de uso único: depois de cada envio recusado, quem usa o
 * componente chama ref.reset() para o visitante resolver de novo.
 */
const TurnstileWidget = forwardRef(({ onToken, onLoadError }, ref) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    // Os callbacks mudam a cada render do pai; a ref evita recriar o widget por causa disso.
    const callbacksRef = useRef({ onToken, onLoadError });
    callbacksRef.current = { onToken, onLoadError };

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
            }
            callbacksRef.current.onToken?.('');
        },
    }));

    useEffect(() => {
        let cancelled = false;

        loadTurnstileScript()
            .then((turnstile) => {
                if (cancelled || !containerRef.current) {
                    return;
                }
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    language: 'pt-br',
                    // As telas públicas são claras; sem isso o widget segue o tema do sistema.
                    theme: 'light',
                    callback: (token) => callbacksRef.current.onToken?.(token),
                    'expired-callback': () => callbacksRef.current.onToken?.(''),
                    'error-callback': () => callbacksRef.current.onToken?.(''),
                });
            })
            .catch(() => {
                if (!cancelled) {
                    callbacksRef.current.onLoadError?.();
                }
            });

        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    return <div ref={containerRef} className="turnstile-widget" />;
});

export default TurnstileWidget;
